import { useEffect, useMemo, useState } from "react";
import { MapPin, Clock, Navigation, ExternalLink, Route, Sparkles } from "lucide-react";
import { api, asset } from "../lib/api";
import PageHero from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { openBookingModal } from "../lib/bookingModal";

const CATEGORY_ORDER = ["History and museums", "Parks and outdoor activities", "Food and dining", "Day trips and nearby towns"];

export default function Visit() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");

  useEffect(() => { api.get("/attractions").then((r) => setItems(r.data)).catch(() => {}); }, []);

  const cats = useMemo(() => {
    const seen = [...new Set(items.map((i) => i.category))];
    return seen.sort((a, b) => (CATEGORY_ORDER.indexOf(a) + 100) - (CATEGORY_ORDER.indexOf(b) + 100));
  }, [items]);

  const filtered = filter === "All" ? items : items.filter((i) => i.category === filter);
  const grouped = cats.reduce((acc, c) => { acc[c] = filtered.filter((i) => i.category === c); return acc; }, {});

  return (
    <div>
      <PageHero
        overline="Explore The Region"
        title="Visit Holbrook"
        subtitle="Holbrook is one of the Hume Highway's most character-filled country stops — home to the iconic HMAS Otway submarine, charming heritage museums, beautiful gardens and warm country welcomes. From history and outdoor adventures to legendary bakeries and easy day trips into the Riverina, our guests are perfectly placed to explore it all — right from our doorstep."
        image="/static/generated/byer-front-day.png"
      />

      <section className="bg-[#F7F5F0] py-20 md:py-24" data-testid="visit-page">
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          {/* Intro / travel-planner strip */}
          <Reveal>
            <div className="grid md:grid-cols-3 gap-5 mb-14">
              {[
                ["10+", "Local attractions", "From museums to gardens and day trips"],
                ["3 min", "To the Submarine", "Holbrook's most famous landmark"],
                ["45 min", "To Albury", "Regional hub on the Murray River"],
              ].map(([n, l, s]) => (
                <div key={l} className="bg-white rounded-2xl p-6 shadow-sm border border-[#0B192C]/5">
                  <p className="font-serif text-4xl text-[#D4AF37]">{n}</p>
                  <p className="text-[#0B192C] font-medium mt-1">{l}</p>
                  <p className="text-xs text-[#222]/55 mt-1">{s}</p>
                </div>
              ))}
            </div>
          </Reveal>

          {/* Category filter */}
          <div className="flex flex-wrap gap-2 mb-10" data-testid="visit-filters">
            {["All", ...cats].map((c) => (
              <button
                key={c}
                onClick={() => setFilter(c)}
                className={`px-4 py-2 rounded-full text-sm border transition-colors ${filter === c ? "bg-[#0B192C] text-white border-[#0B192C]" : "bg-white text-[#0B192C] border-[#0B192C]/15 hover:border-[#D4AF37]"}`}
                data-testid={`visit-filter-${c}`}
              >
                {c}
              </button>
            ))}
          </div>

          {items.length === 0 && <p className="text-center text-[#222]/50">Attractions coming soon.</p>}

          <div className="space-y-16">
            {cats.filter((c) => grouped[c].length > 0).map((cat) => (
              <div key={cat} data-testid="visit-cat">
                <Reveal>
                  <h2 className="font-serif text-3xl md:text-4xl text-[#0B192C] mb-8 flex items-center gap-4">
                    {cat}<span className="flex-1 gold-line" />
                  </h2>
                </Reveal>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {grouped[cat].map((a, idx) => (
                    <Reveal key={a.id} delay={(idx % 3) * 0.08}>
                      <article className="group rounded-2xl overflow-hidden bg-white shadow-sm border border-[#0B192C]/5 h-full flex flex-col hover:shadow-lg hover:-translate-y-1 transition-all duration-300" data-testid={`attraction-${a.id}`}>
                        <div className="h-48 bg-[#0B192C]/5 overflow-hidden relative">
                          {a.image ?
                            <img src={asset(a.image)} alt={a.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />
                            : <div className="w-full h-full grid place-items-center text-[#0B192C]/20 bg-gradient-to-br from-[#0B192C]/5 to-[#D4AF37]/10"><MapPin size={40} /></div>}
                          {(a.distance || a.travel_time) && (
                            <div className="absolute top-3 right-3 bg-white/95 backdrop-blur rounded-full px-3 py-1 text-xs text-[#0B192C] font-medium flex items-center gap-1.5 shadow-sm">
                              <Route size={12} className="text-[#D4AF37]" />
                              {a.distance}{a.distance && a.travel_time ? " · " : ""}{a.travel_time}
                            </div>
                          )}
                        </div>
                        <div className="p-6 flex flex-col flex-1">
                          <h3 className="font-serif text-xl md:text-2xl text-[#0B192C]">{a.name}</h3>
                          <p className="text-sm text-[#222]/65 mt-2 leading-relaxed flex-1">{a.description}</p>
                          {a.address && (
                            <p className="text-xs text-[#222]/50 mt-4 flex items-start gap-1.5">
                              <MapPin size={13} className="text-[#D4AF37] mt-0.5 shrink-0" /> {a.address}
                            </p>
                          )}
                          <div className="flex flex-wrap gap-2 mt-5">
                            {a.directions_url && (
                              <a href={a.directions_url} target="_blank" rel="noreferrer" data-testid="attraction-directions"
                                className="flex items-center gap-1.5 text-sm bg-[#0B192C] text-white px-4 py-2 rounded-full hover:bg-[#D4AF37] hover:text-[#0B192C] transition-colors">
                                <Navigation size={14} /> Get Directions
                              </a>
                            )}
                            {a.website_url && (
                              <a href={a.website_url} target="_blank" rel="noreferrer"
                                className="flex items-center gap-1.5 text-sm border border-[#0B192C]/20 text-[#0B192C] px-4 py-2 rounded-full hover:bg-[#0B192C] hover:text-white transition-colors">
                                <ExternalLink size={14} /> More info
                              </a>
                            )}
                          </div>
                        </div>
                      </article>
                    </Reveal>
                  ))}
                </div>
              </div>
            ))}
          </div>

          {/* CTA */}
          <Reveal>
            <div className="mt-20 relative overflow-hidden rounded-3xl bg-[#0B192C] p-10 md:p-14 text-center grain">
              <div className="absolute inset-0 opacity-30 pointer-events-none"
                style={{ background: "radial-gradient(600px 300px at 20% 30%, #D4AF3733, transparent 60%)" }} />
              <div className="relative z-10 max-w-2xl mx-auto">
                <Sparkles size={28} className="text-[#D4AF37] mx-auto mb-3" />
                <h3 className="font-serif text-3xl md:text-4xl text-white">Make Byer Fountain your base for Holbrook</h3>
                <p className="text-white/70 mt-4 font-light">Stay with us and step out into all the region has to offer — comfortable ground-floor rooms, easy parking and warm country hospitality all just moments from the Hume Highway.</p>
                <button onClick={openBookingModal}
                  className="mt-6 inline-flex items-center gap-2 bg-[#D4AF37] text-[#0B192C] px-6 py-3 rounded-full font-medium hover:bg-white transition-colors"
                  data-testid="visit-book-now">
                  <Clock size={16} /> Plan your stay
                </button>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
